import React, { useState, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form } from 'react-bootstrap';
import Select from 'react-select';
import { FaPlus } from 'react-icons/fa';
import { AuthContext } from '../contexts/AuthContext';
import CRTSubmissionModal from './CRTSubmissionModal';

const API_URL = process.env.REACT_APP_API_URL;

const CrtSearch = ({ placeholder = "Search for your CRT model...", className = "", compact = false }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [modelInput, setModelInput] = useState('');
  const [selectedCrtModel, setSelectedCrtModel] = useState(null);
  const [showNewCrtModal, setShowNewCrtModal] = useState(false);
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  // Handle CRT model selection
  const handleCrtModelChange = (selected) => {
    if (selected && selected.value === 'submit_new') {
      setShowNewCrtModal(true);
    } else if (selected && selected.slug) {
      navigate(`/crts/${selected.slug}`);
    } else if (selected) {
      setSelectedCrtModel(selected);
    } else {
      setSelectedCrtModel(null);
    }
  };

  // Debounce function
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Fetch CRT model suggestions with debounce
  const debouncedFetch = useCallback(
    debounce(async (inputValue) => {
      if (inputValue.length < 2) {
        setSuggestions([]);
        return;
      }

      setIsLoadingModels(true);

      try {
        const res = await fetch(`${API_URL}/crts/autocomplete?q=${encodeURIComponent(inputValue)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        const formatted = (data.suggestions || []).map((item) => ({
          value: item.title,
          label: item.title,
          slug: item.slug || null
        }));

        setSuggestions(formatted);
      } catch (err) {
        console.error('Autocomplete error:', err);
        setSuggestions([]);
      } finally {
        setIsLoadingModels(false);
      }
    }, 300),
    [token]
  );

  const customStyles = {
    container: (provided) => ({
      ...provided,
      minWidth: compact ? '200px' : '300px',
      maxWidth: compact ? '400px' : '100%',
    }),
    control: (provided) => ({
      ...provided,
      fontSize: compact ? '14px' : '16px',
      backgroundColor: '#1a0933',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      paddingTop: '0',
      paddingBottom: '0',
      fontSize: '16px',
      color: 'white'
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: compact ? '0 6px' : '5px 10px',
    }),
    option: (provided, state) => ({
      ...provided,
      padding: compact ? '5px 8px' : '8px 15px',
      fontSize: compact ? '14px' : '16px',
      backgroundColor: state.isFocused ? '#2a1245' : '#1a0933',
      color: 'white',
      '&:hover': {
        backgroundColor: '#FC1239'
      },
      cursor: 'pointer'
    }),
    menu: (provided) => ({
      ...provided,
      border: '1px solid #4a4a4a',
      marginTop: '8px',
      zIndex: 1,
      overflow: 'hidden',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
    }),
    menuList: (provided) => ({
      ...provided,
      padding: '0',
      backgroundColor: '#fca311',
      '&:hover': {
        backgroundColor: '#b8e986',
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      color: 'white'
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#999'
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: '#000000',
      backgroundColor: '#fca311',
      fontWeight: '500',
      lineHeight: '1.5',
      textAlign: 'left'
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      padding: compact ? '5px 8px' : '8px 15px',
      fontSize: compact ? '14px' : '16px',
      cursor: 'pointer',
      color: '#000000',
      fontWeight: '500',
      lineHeight: '1.5'
    }),
  };

  // Custom NoOptionsMessage component
  const NoOptionsMessage = props => {
    // Only show the message if there's input
    if (!modelInput || modelInput.length < 3) {
      return null;
    }

    return (
      <div
        {...props.innerProps}
        style={{
          padding: compact ? '5px 8px' : '8px 15px',
          color: '#000000',
          fontWeight: '500',
          cursor: 'pointer',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
          lineHeight: '1.5'
        }}
        onClick={() => setShowNewCrtModal(true)}
      >
        {compact ? 
          <><FaPlus style={{ verticalAlign: 'middle', marginRight: '5px', marginBottom: '2px' }} /> Add "{modelInput}"</> :
          <><FaPlus style={{ verticalAlign: 'middle', marginRight: '5px', marginBottom: '2px' }} /> Can't find {modelInput.length < 3 ? '' : `"${modelInput}"`}? Add yours! Ask for support!</>
        }
      </div>
    );
  };

  return (
    <div className={className}>
      <Form.Group className="mb-0">
        <Select
          isClearable
          isSearchable
          isLoading={isLoadingModels}
          options={suggestions}
          value={selectedCrtModel}
          onInputChange={(inputValue, { action }) => {
            if (action === 'input-change') {
              setModelInput(inputValue);
              debouncedFetch(inputValue);
            }
            if (action === 'menu-close') {
              setModelInput('');
            }
          }}
          inputValue={modelInput}
          onChange={handleCrtModelChange}
          placeholder={placeholder}
          classNamePrefix="dark-select"
          styles={customStyles}
          components={{ 
            DropdownIndicator: () => null,
            IndicatorSeparator: () => null,
            NoOptionsMessage
          }}
          noOptionsMessage={({ inputValue }) => inputValue && inputValue.length > 2 ? <NoOptionsMessage /> : null}
        />
      </Form.Group>

      <CRTSubmissionModal
        show={showNewCrtModal}
        onHide={() => {
          setShowNewCrtModal(false);
          setSelectedCrtModel(null);
        }}
        existingCrtTitle={modelInput}
      />
    </div>
  );
};

export default CrtSearch; 